import { cloneDeep, orderBy } from 'lodash';
import { FontServer, parseLayerId } from '@meitu/saas-editor-core/es/framework';
import { traverseTrees } from '@meitu/saas-editor-core/es/utils/tree-kit';
import { ILayerProps, ITextLayerProps, LayerType } from '@meitu/saas-editor-core/es/version-types';
import { editor } from '@/instance';
import { getJSONByUrl } from '@/utils/request';
import { transferTemplateToClient } from '../server-to-client';
import { getNewLayers, IPPTContentProps, Layer, Page, PPTTemplate } from './apply-ppt-template';

export const getStagePoints = (layers: ILayerProps[], page_type: string) => {
    const allLayers = [] as ILayerProps[];
    traverseTrees(layers, (layer: ILayerProps) => {
        if (layer.layerType === LayerType.TEXT_LAYER) {
            allLayers.push(layer);
        }
    });

    const indexes = [] as number[];
    const titleIndexes = [] as number[];
    const contentIndexes = [] as number[];
    for (let i = 0; i < allLayers.length; i++) {
        const layer = allLayers[i] as ILayerProps;
        const { extraInfo } = layer;

        if (!extraInfo || (extraInfo && !extraInfo.label)) {
            continue;
        }

        const { type, content_index, isOld } = extraInfo.label;

        if (!type) {
            continue;
        }

        if (page_type === '目录' && type !== '目录标题') {
            continue;
        }

        if (page_type === '内容' && type !== '列表标题' && type !== '列表内容') {
            continue;
        }

        if (page_type === '目录' && type === '目录标题') {
            indexes.push(content_index);
        }

        if (page_type === '内容' && type === '列表标题') {
            titleIndexes.push(content_index);
        }

        if (page_type === '内容' && type === '列表内容') {
            contentIndexes.push(content_index);
        }
    }

    if (page_type === '内容') {
        for (let i = 0; i < titleIndexes.length; i++) {
            const index = titleIndexes[i];
            const subI = contentIndexes.findIndex((item) => item == index);
            if (subI >= 0) {
                contentIndexes.splice(subI, 1);
            }
        }

        return contentIndexes.length + titleIndexes.length;
    }

    return indexes.length;
};

export const removeOldComponentLayer = (layers: ILayerProps[]) => {
    const removeIds = [] as string[];

    traverseTrees(layers, (layer: ILayerProps) => {
        const { extraInfo } = layer;

        if (layer.layerType === LayerType.GROUP_LAYER) {
            return;
        }

        if (!extraInfo || (extraInfo && !extraInfo.label)) {
            return;
        }

        const { canReplace } = extraInfo.label;

        if (canReplace) {
            return;
        }

        removeIds.push(layer.id);
    });

    const removeLayerById = (layers: ILayerProps[]) => {
        const newLayers = layers.filter((layer) => !removeIds.find((id) => layer.id === id));

        for (let i = 0; i < newLayers.length; i++) {
            if (newLayers[i].children.length > 0) {
                newLayers[i].children = removeLayerById(newLayers[i].children);
            }
        }
        return newLayers;
    };

    let resultLayers = removeLayerById(cloneDeep(layers));

    const removeEmptyGroupLayers = (layers: ILayerProps[]) => {
        const newLayers = cloneDeep(layers).filter((layer) => {
            return !(layer.layerType === LayerType.GROUP_LAYER && layer.children.length === 0);
        });
        for (let i = 0; i < newLayers.length; i++) {
            if (newLayers[i].children.length > 0) {
                newLayers[i].children = removeEmptyGroupLayers(newLayers[i].children);
            }
        }

        for (let i = 0; i < newLayers.length; i++) {
            newLayers[i].id = parseLayerId(newLayers[i].id)?.id || '';
        }
        return newLayers;
    };

    resultLayers = removeEmptyGroupLayers(resultLayers);
    return resultLayers;
};

const getContentByStage = async (layers: ILayerProps[], page_type: string, json_url: string) => {
    const content = {
        page_type,
        data: {},
        json_url: '',
    } as IPPTContentProps;

    const allLayers = [] as ILayerProps[];
    traverseTrees(layers, (layer: ILayerProps) => {
        if (layer.layerType === LayerType.TEXT_LAYER) {
            allLayers.push(layer);
        }
    });

    const idIndexDirectoryMap = {} as any;
    const idIndexTitleMap = {} as any;
    const idIndexContentMap = {} as any;
    for (let i = 0; i < allLayers.length; i++) {
        const layer = allLayers[i] as ILayerProps;
        const { extraInfo } = layer;

        if (!extraInfo || (extraInfo && !extraInfo.label) || layer.layerType !== LayerType.TEXT_LAYER) {
            continue;
        }

        const { type, content_index, isOld } = extraInfo.label;

        if (!type) {
            continue;
        }

        if (type !== '列表标题' && type !== '列表内容' && type !== '目录标题') {
            continue;
        }

        const text = (layer.data as ITextLayerProps).text;
        if (type === '目录标题') {
            idIndexDirectoryMap[layer.id] = {
                content_index,
                id: layer.id,
                text,
            };
        } else if (type === '列表标题') {
            idIndexTitleMap[layer.id] = { content_index, id: layer.id, text };
        } else if (type === '列表内容') {
            idIndexContentMap[layer.id] = { content_index, id: layer.id, text };
        }
    }

    if (page_type === '目录') {
        const result = orderBy(Object.values(idIndexDirectoryMap), ['content_index']);
        result.forEach((item: any, index) => {
            content.data[`目录标题:${index + 1}`] = item.text;
        });
    } else if (page_type === '内容') {
        const titleList = orderBy(Object.values(idIndexTitleMap), ['content_index']);
        const contentList = orderBy(Object.values(idIndexContentMap), ['content_index']) as any[];

        titleList.forEach((item: any, index) => {
            content.data[`列表标题:${index + 1}`] = item.text;
            const contentIndex = contentList.findIndex(
                (contentItem: any) => contentItem.content_index == item.content_index,
            );

            if (contentIndex >= 0) {
                content.data[`列表内容:${index + 1}`] = contentList[contentIndex].text;
                contentList.splice(contentIndex, 1);
            } else {
                content.data[`列表内容:${index + 1}`] = ' ';
            }
        });

        contentList.forEach((item: any, index) => {
            content.data[`列表内容:${titleList.length + index + 1}`] = item.text;
            content.data[`列表标题:${titleList.length + index + 1}`] = ' ';
        });
    }

    console.log('content===', content);
    return content;
};

/**
 *
 *  FIXME
 *
 *  替换组件规则：
 *  1、获取旧组件的文本数据(顺序不保证, 只保证数据不丢失)
 *  2、获取新添加的图层
 *  3、删除所有旧的组件图层和新添加的图层  只留下套版图层
 *  4、新的组件应用主题和替换文本
 *  5、将新的组件放到舞台里面
 *  6、将新增加的图层放到舞台里面 (新添加的图层会被放到最上层)
 */
export const replaceComponentKit = async (
    componentUrl: string,
    templateUrl: string,
    layers: ILayerProps[],
    fonts: any[],
    page_type: string,
    themeType: string,
) => {
    let replaceComponent = await getJSONByUrl(componentUrl);
    const template = await getJSONByUrl(templateUrl);

    const templateData = await transferTemplateToClient(template, fonts, false, false, true);
    const clientData = await transferTemplateToClient(replaceComponent, fonts, false, false, true);

    replaceComponent = clientData.template;

    const content = await getContentByStage(layers, page_type, '');
    const newLayers = getNewLayers(layers);
    const applyLayers = removeOldComponentLayer(layers);
    const theme = PPTTemplate.getTheme({
        ...templateData.template,
        extraInfo: {
            ...templateData.extraInfo,
            ...(template.extraInfo || {}),
        },
    });

    themeType === '浅色调' ? (theme.colors = theme.darkColors) : (theme.colors = theme.lightColors);
    replaceComponent.templateConf[0].layers.splice(0, 1);

    const resultLayers = [...applyLayers, ...replaceComponent.templateConf[0].layers];

    traverseTrees(replaceComponent.templateConf[0].layers, (layer: ILayerProps) => {
        if (layer.layerType !== LayerType.GROUP_LAYER) {
            const { extraInfo } = layer;
            if (!extraInfo) {
                layer.extraInfo = {};
                layer.extraInfo.label = { isOld: true };
            } else {
                extraInfo.label = {
                    ...extraInfo.label,
                    isOld: true,
                };
            }
        }
    });
    Page.replaceData(content, resultLayers, theme);

    const allTextLayers = [] as ILayerProps[];
    traverseTrees(resultLayers, (layer: ILayerProps) => {
        if (layer.layerType === LayerType.TEXT_LAYER) {
            allTextLayers.push(layer);
        }
    });
    // [delete] await createFontFamilySubset(allTextLayers, fonts);

    const fontServer = new FontServer(editor.id);
    fontServer.setList(fonts);
    await fontServer.createLayersFontSubset(allTextLayers);
    traverseTrees(allTextLayers, (layer: ILayerProps) => {
        if (layer.layerType === LayerType.TEXT_LAYER) {
            const { extraInfo } = layer;

            if (extraInfo?.label) {
                const { textExpend } = extraInfo.label;
                if (textExpend) {
                    Layer.adjustTextSize(layer, textExpend);
                }
            }
        }
    });
    return [...resultLayers, ...newLayers];
};
